import { TreeNodeProps } from 'antd';
import classNames from 'classnames';
import { useCallback, useMemo, useRef, useState } from 'react';
import { CURRENT_FOLDER_QK } from '@/modules/admin-dashboard/pages/Documents.tsx';
import {
  useFoldersTreeQuery,
  useMoveDocumentMutation,
  useMoveFolderMutation,
} from '@/modules/admin-dashboard/queries/documents-folders.ts';
import {
  BaseModalProps,
  Button,
  DefaultOptionType,
  Icon,
  ModalContentWrapper,
  TreeSelect,
  TreeSelectProps,
} from 'ui';
import { useParseNumericQueryParam } from 'ui/hooks';
import { ChevronDownIcon, FolderIcon, SearchIcon } from 'ui/icons';
import { DocumentFolderTreeNode } from 'compliance-api';

const treeSelectStyle = {
  width: '100%',
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  height: 40,
};

const switcherIcon = (opener: TreeNodeProps) => (
  <img
    alt=""
    width={16}
    src={ChevronDownIcon}
    className={classNames({ 'rotate-180': opener.expanded })}
  />
);

const treeTitleRender: TreeSelectProps['treeTitleRender'] = item => {
  return (
    <div
      className="flex gap-3 items-center"
      title={item.disabled && item.disabledReason ? item.disabledReason : undefined}
    >
      <Icon Source={FolderIcon} rawColor={item.color || undefined} />
      <span>
        {typeof item.title === 'function' ? item.title({ key: item.key || 0 }) : item.title}
      </span>
    </div>
  );
};

interface DocumentMovingModalProps extends BaseModalProps {
  isFolder?: boolean;
  itemId: number;
  itemName: string;
  clientId?: number;
}

const DocumentMovingModal = ({
  open,
  itemId,
  onClose,
  itemName,
  isFolder = false,
  clientId,
}: DocumentMovingModalProps) => {
  const [selectedItem, setSelectedItem] = useState<number>();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const currentFolderId = useParseNumericQueryParam(CURRENT_FOLDER_QK);

  const { data: treeData } = useFoldersTreeQuery({
    filter_by_client_id: clientId,
  });

  const { mutateAsync: moveDocument, isLoading: isDocumentMovingLoading } =
    useMoveDocumentMutation();

  const { mutateAsync: moveFolder, isLoading: isFolderMovingLoading } = useMoveFolderMutation();

  const handleMove = useCallback(async () => {
    if (isFolder) {
      await moveFolder({
        folder_id: itemId,
        new_parent_folder_id: selectedItem || undefined,
      });
    } else {
      await moveDocument({
        document_id: itemId,
        folder_id: selectedItem || undefined,
      });
    }

    onClose();
  }, [isFolder, itemId, moveDocument, moveFolder, onClose, selectedItem]);

  const normalizeTreeNode = useCallback(
    (
      node: DocumentFolderTreeNode,
      disableOptions?: {
        disabledNested?: boolean;
      },
    ): DefaultOptionType => {
      const { disabledNested = false } = disableOptions || {};
      const sameFolderOriginDisable = isFolder && node.id === itemId;

      const resolveDisableNested = sameFolderOriginDisable || disabledNested;
      const isCurrentDirectory = currentFolderId === node.id;

      let disabledReason;

      if (isFolder && (sameFolderOriginDisable || disabledNested)) {
        disabledReason = 'Cannot move directory into itself.';
      }

      if (isFolder && isCurrentDirectory) {
        disabledReason = 'The directory is already in this directory.';
      }

      if (!isFolder && isCurrentDirectory) {
        disabledReason = 'The file is already in this directory.';
      }
      return {
        key: node.id,
        title: node.name,
        value: node.id,
        color: node.color,
        disabled: isCurrentDirectory || resolveDisableNested,
        disabledReason,
        children: (node.children || []).map((node: DocumentFolderTreeNode) =>
          normalizeTreeNode(node, {
            disabledNested: resolveDisableNested,
          }),
        ),
      };
    },
    [currentFolderId, isFolder, itemId],
  );

  const normalizedTreeData = useMemo(
    () => [
      {
        title: 'Documents',
        value: 0,
        color: '#55F',
        disabled: !currentFolderId,
        disabledReason: 'Cannot move: same as current location.',
        children: (treeData?.tree || []).map(node => normalizeTreeNode(node)),
      },
    ],
    [currentFolderId, normalizeTreeNode, treeData?.tree],
  );

  return (
    <ModalContentWrapper
      title={`Move ${isFolder ? 'folder' : 'file'} "${itemName}"`}
      open={open}
      onClose={onClose}
    >
      <TreeSelect
        open
        showSearch
        treeLine
        treeDefaultExpandAll
        popupMatchSelectWidth
        prefix={<img src={SearchIcon} alt="search" />}
        getPopupContainer={() => containerRef.current!}
        filterTreeNode={(value, { title }) =>
          (title || '').toString().toLowerCase().includes(value.toLowerCase())
        }
        onSelect={setSelectedItem}
        placeholder="Search"
        className="[&>div]:!rounded-b-none"
        style={treeSelectStyle}
        suffixIcon={null}
        switcherIcon={switcherIcon}
        treeTitleRender={treeTitleRender}
        treeData={normalizedTreeData}
        listItemHeight={36}
      />
      <div
        ref={containerRef}
        className="border-l border-b border-r rounded-b  [&>div]:!w-full [&>div]:shadow-none [&_.ant-select-tree-list-holder>div]:!overflow-y-auto  [&>div]:static relative [&_.ant-select-tree-list-holder-inner]:!transform-none [&_.ant-select-tree-list-scrollbar]:!hidden"
      />
      <Button
        disabled={
          typeof selectedItem !== 'number' || isDocumentMovingLoading || isFolderMovingLoading
        }
        loading={isDocumentMovingLoading || isFolderMovingLoading}
        className="mt-8"
        onClick={handleMove}
      >
        Move
      </Button>
    </ModalContentWrapper>
  );
};

export default DocumentMovingModal;
